import { useState, FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { resetPassword } from '../../core/auth/supabase-auth';
import AuthCard from '../../features/auth/components/AuthCard';

const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await resetPassword(email.trim().toLowerCase());
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError((err as Error).message || t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title={t('forgotPassword.title')}
      titleAccent={t('forgotPassword.titleAccent')}
      subtitle={t('forgotPassword.subtitle')}
      footer={
        <Link
          to="/login"
          className="font-medium text-brand-navy underline decoration-brand-teal decoration-2 underline-offset-4 hover:text-brand-teal transition-colors"
        >
          {t('forgotPassword.backToLogin')}
        </Link>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        {success && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
          >
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
            <span>{t('forgotPassword.successMessage')}</span>
          </div>
        )}

        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="block text-[13px] font-medium text-brand-ink"
          >
            {t('forgotPassword.emailLabel')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder={t('forgotPassword.emailPlaceholder')}
            className="mt-1.5 block w-full rounded-lg border border-brand-rule/30 bg-card px-3.5 py-2.5 text-[15px] text-brand-ink placeholder:text-brand-fog/70 transition focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/25 disabled:opacity-60"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="group relative inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-teal px-6 text-[14px] font-semibold tracking-tight text-brand-navy transition-all hover:bg-brand-tealLight hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-brand-teal"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('forgotPassword.sendingButton')}
            </>
          ) : (
            <>
              {t('forgotPassword.submitButton')}
              <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </>
          )}
        </button>
      </form>
    </AuthCard>
  );
};

export default ForgotPasswordPage;
